// Экран прохождения части квеста — общий модуль единого режима квестов (ПК и МК, решение 15.10).
// Без React и без зависимостей: рисует в переданный элемент, с сервером говорит через адаптер, тексты — снаружи.
// Сервер решает всё (время, правильность, очки); модуль только показывает вопрос, считает таймер для глаз и отправляет ответ.

export type Media = { type: 'image' | 'audio' | 'video'; src: string; poster?: string | null; label?: string | null } | null;
export type Option = string | { text: string; image: string | null };
export type Question = {
  n: number; type: 'choice' | 'open' | 'theme'; text: string | null; time_sec: number;
  media: Media; media_extra: Media; media_hint: Media; options?: Option[]; letters?: number | number[] | null;
};
export type PlayState = { play_id: number; next_q: number; score: number; theme_pending: boolean; theme_options: string[] | null;
                          results: { ok: boolean; pts: number }[]; hints?: Record<string, any>; extra?: Record<string, number> };
export type HintPrices = { fifty: number; letter: number; theme: number; retry: number; time: number; time_sec: number };
export type Start = { questions: Question[]; max_score: number; state: PlayState; part: { n: number; kind: string; title: string | null };
                      hints?: { prices: HintPrices; balance: number } | null };   // null — подсказок нет (гость, повтор)
export type Adapter = {
  serve(q: number): Promise<{ elapsed_sec: number }>;
  answer(q: number, answer: unknown, stage?: 'theme_options'): Promise<any>;
  hint?(q: number, kind?: 'retry'): Promise<any>;
  time?(q: number): Promise<{ sec: number; balance: number }>;
};
export type Texts = {
  question: string;          // «Вопрос {n} из {total}»
  score: string;             // «{score} очк.»
  answerPlaceholder: string; themePlaceholder: string; submit: string; next: string; finish: string;
  correct: string; wrong: string; timeout: string; points: string;   // «+{points}»
  rightAnswer: string;       // «Правильный ответ: {answer}»
  themeOptions: string;      // «Не угадали — выберите из вариантов»
  error: string;
  // подсказки: {price} — цена в валюте платформы (печеньки / нейроны), {sec} — секунды
  hint: string;              // «💡 Подсказка · {name} · {price}»
  hintNames: { fifty: string; letter: string; theme: string };
  retry: string;             // «🛡 Право на ошибку · {price}»
  time: string;              // «⏱ +{sec} с · {price}»
  fiftyUsed: string; letterUsed: string; themeUsed: string; retryOn: string; retryUsed: string; timeUsed: string;
  notEnough: string;         // «Не хватает: нужно {price}»
  price: (n: number) => string;
};
export type Opts = { texts: Texts; onFinish: (r: any) => void; onError?: (e: unknown) => void };

const fill = (s: string, v: Record<string, string | number>) => s.replace(/\{(\w+)\}/g, (_, k) => String(v[k] ?? ''));

function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, any> = {}, ...kids: (Node | string | null | false | undefined)[]) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === 'class') e.className = v;
    else if (k.startsWith('on')) (e as any)[k.toLowerCase()] = v;
    else if (k in e && typeof v !== 'string') (e as any)[k] = v;
    else e.setAttribute(k, String(v));
  }
  for (const c of kids) if (c != null && c !== false) e.append(c);
  return e;
}

function mediaEl(m: Media) {
  if (!m) return null;
  const label = m.label ? h('div', { class: 'qp-label' }, m.label) : null;
  if (m.type === 'image') return h('div', { class: 'qp-media' }, h('img', { src: m.src, alt: '', loading: 'eager' }), label);
  // звук с картинкой (ответ: кадр + песня) — картинка над плеером
  if (m.type === 'audio') return h('div', { class: 'qp-media audio' }, m.poster ? h('img', { src: m.poster, alt: '' }) : null, label,
    h('audio', { src: m.src, controls: true, preload: 'auto' }));
  return h('div', { class: 'qp-media' }, h('video', { src: m.src, poster: m.poster ?? undefined, controls: true, playsinline: '', preload: 'metadata' }), label);
}

// клетки темы: [9, 7] — два слова; заполняются по мере ввода
function cells(letters: number | number[], value: string) {
  const words = Array.isArray(letters) ? letters : [letters];
  const chars = value.replace(/\s+/g, ' ').trim().split(' ');
  return h('div', { class: 'qp-cells', 'aria-hidden': 'true' }, ...words.map((len, w) =>
    h('div', { class: 'qp-word' }, ...Array.from({ length: len }, (_, i) => h('span', { class: 'qp-cell' }, (chars[w] ?? '')[i]?.toUpperCase() ?? '')))));
}

export function mountPlayer(root: HTMLElement, start: Start, api: Adapter, opts: Opts) {
  const T = opts.texts;
  const qs = start.questions;
  let score = start.state.score;
  const H = start.hints ?? null;
  let balance = H?.balance ?? 0;
  const bought: Record<string, any> = { ...(start.state.hints ?? {}) };
  const extra: Record<string, number> = { ...(start.state.extra ?? {}) };
  let timer = 0;
  let alive = true;
  root.classList.add('qp');

  const stop = () => { clearInterval(timer); timer = 0; };
  const fail = (e: unknown) => { stop(); opts.onError?.(e); };
  const render = (...kids: (Node | null)[]) => { if (alive) root.replaceChildren(...kids.filter(Boolean) as Node[]); };
  const head = (q: Question) => h('div', { class: 'qp-head' },
    h('span', {}, fill(T.question, { n: q.n, total: qs.length })), h('span', { class: 'qp-score' }, fill(T.score, { score })));

  async function show(i: number) {
    const q = qs[i];
    if (!q) return;
    let elapsed = 0;
    try { elapsed = (await api.serve(q.n)).elapsed_sec; } catch (e) { return fail(e); }
    let sent = false;
    let limit = q.time_sec + (extra[q.n] ?? 0);
    const bar = h('div', { class: 'qp-timer' }, h('i'));
    const t0 = Date.now() - elapsed * 1000;
    const tick = () => {
      const left = Math.max(0, limit - (Date.now() - t0) / 1000);
      (bar.firstChild as HTMLElement).style.width = `${(left / limit) * 100}%`;
      const tb = root.querySelector<HTMLButtonElement>('.qp-time');
      if (tb) tb.hidden = left <= 3;
      bar.classList.toggle('low', left < 10);
      if (left <= 0 && !sent) send(q.type === 'choice' ? 0 : '');
    };

    async function send(answer: unknown, picked?: HTMLElement) {
      if (sent) return;
      sent = true;
      stop();
      root.querySelectorAll('button, input').forEach(b => ((b as HTMLButtonElement).disabled = true));
      picked?.classList.add('picked');
      let r: any;
      try { r = await api.answer(q.n, answer); } catch (e) { return fail(e); }
      if (r.retry) {   // право на ошибку: вариант гаснет, правильный не раскрыт, время идёт дальше
        sent = false;
        bought[q.n] = { kind: 'retry', used: true, wrong: answer };
        root.querySelectorAll<HTMLButtonElement>('button, input').forEach(b => (b.disabled = b.classList.contains('off')));
        root.querySelectorAll('.picked').forEach(b => b.classList.remove('picked'));
        if (q.type === 'choice') applyHint(q, bought[q.n]);
        else { const inp = root.querySelector<HTMLInputElement>('.qp-form input'); if (inp) { inp.value = ''; inp.focus(); } setBar(T.retryUsed); }
        timer = window.setInterval(tick, 250);
        return;
      }
      score += r.points ?? 0;
      if (r.theme_options) return themeOptions(q, r.theme_options);
      reveal(q, r, answer);
    }

    // подсказки: одна на вопрос (💡 или 🛡) и отдельно ⏱; цены и баланс — от сервера, решает сервер
    const hintBar = h('div', { class: 'qp-hints' });
    const setBar = (text: string) => { hintBar.querySelectorAll('.qp-hint:not(.qp-time), .qp-used:not(.qp-tused)').forEach(b => b.remove()); hintBar.prepend(h('span', { class: 'qp-used' }, text)); };
    function applyHint(q2: Question, x: any) {
      if (x.kind === 'fifty') {
        x.off.forEach((k: number) => { const b = root.querySelector<HTMLButtonElement>(`.qp-opt[data-k="${k}"]`); if (b) { b.classList.add('off'); b.disabled = true; } });
        setBar(T.fiftyUsed);
      } else if (x.kind === 'letter') setBar(fill(T.letterUsed, { mask: [...x.mask].join(' '), n: x.length }));
      else if (x.kind === 'theme') setBar(fill(T.themeUsed, { letters: x.letters.join(' · ') }));
      else if (x.kind === 'retry') {
        if (x.used && q2.type === 'choice') { const b = root.querySelector<HTMLButtonElement>(`.qp-opt[data-k="${x.wrong}"]`); if (b) { b.classList.add('off', 'bad'); b.disabled = true; } }
        setBar(x.used ? T.retryUsed : T.retryOn);
      }
    }
    if (H && api.hint) {
      const kind = q.type === 'choice' ? ((q.options?.length ?? 0) >= 3 ? 'fifty' : null) : q.type === 'open' ? 'letter' : 'theme';
      const retryOk = (q.type === 'choice' && (q.options?.length ?? 0) >= 3) || q.type === 'open';
      const buy = async (b: HTMLButtonElement, k?: 'retry') => {
        const price = H.prices[(k ?? kind) as 'fifty'];
        if (balance < price) return opts.onError?.(Object.assign(new Error(fill(T.notEnough, { price: T.price(price) })), { code: 'not_enough' }));
        b.disabled = true;
        try { const x = await api.hint!(q.n, k); balance = x.balance; bought[q.n] = x; if (!sent) applyHint(q, x); }
        catch (e) { b.disabled = false; opts.onError?.(e); }
      };
      if (!bought[q.n]) {
        if (kind) { const b = h('button', { type: 'button', class: 'qp-hint' }, fill(T.hint, { name: T.hintNames[kind as 'fifty'], price: T.price(H.prices[kind as 'fifty']) })) as HTMLButtonElement; b.onclick = () => buy(b); hintBar.append(b); }
        if (retryOk) { const b = h('button', { type: 'button', class: 'qp-hint' }, fill(T.retry, { price: T.price(H.prices.retry) })) as HTMLButtonElement; b.onclick = () => buy(b, 'retry'); hintBar.append(b); }
      }
      if (api.time) {
        if (extra[q.n]) hintBar.append(h('span', { class: 'qp-used qp-tused' }, fill(T.timeUsed, { sec: extra[q.n] })));
        else {
          const tb = h('button', { type: 'button', class: 'qp-hint qp-time' }, fill(T.time, { sec: H.prices.time_sec, price: T.price(H.prices.time) })) as HTMLButtonElement;
          tb.onclick = async () => {
            if (balance < H.prices.time) return opts.onError?.(Object.assign(new Error(fill(T.notEnough, { price: T.price(H.prices.time) })), { code: 'not_enough' }));
            tb.disabled = true;
            try {
              const x = await api.time!(q.n);
              balance = x.balance; extra[q.n] = x.sec; limit += x.sec;
              tb.replaceWith(h('span', { class: 'qp-used qp-tused' }, fill(T.timeUsed, { sec: x.sec })));
            } catch (e) { tb.disabled = false; opts.onError?.(e); }
          };
          hintBar.append(tb);
        }
      }
    }

    let body: Node;
    if (q.type === 'choice') {
      const opts2 = q.options ?? [];
      const pics = opts2.some(o => typeof o !== 'string' && o.image);
      body = h('div', { class: pics ? 'qp-options pics' : 'qp-options' }, ...opts2.map((o, k) => {
        const text = typeof o === 'string' ? o : o.text;
        const b = h('button', { type: 'button', class: 'qp-opt', 'data-k': k + 1 },
          typeof o !== 'string' && o.image ? h('img', { src: o.image, alt: `${k + 1}` }) : null,
          text ? h('span', {}, text) : pics ? h('span', { class: 'qp-k' }, String(k + 1)) : null);
        b.onclick = () => send(k + 1, b);
        return b;
      }));
    } else {
      const input = h('input', { type: 'text', autocomplete: 'off', autocapitalize: 'off', spellcheck: 'false', enterkeyhint: 'send',
        placeholder: q.type === 'theme' ? T.themePlaceholder : T.answerPlaceholder, 'aria-label': q.type === 'theme' ? T.themePlaceholder : T.answerPlaceholder }) as HTMLInputElement;
      const cellBox = q.type === 'theme' && q.letters ? h('div', {}, cells(q.letters, '')) : null;
      input.oninput = () => cellBox?.replaceChildren(cells(q.letters!, input.value));
      const form = h('form', { class: 'qp-form' }, cellBox, input, h('button', { type: 'submit', class: 'btn' }, T.submit)) as HTMLFormElement;
      form.onsubmit = ev => { ev.preventDefault(); if (input.value.trim()) send(input.value.trim()); };
      body = form;
      setTimeout(() => input.focus({ preventScroll: true }), 50);
    }
    render(head(q), bar, mediaEl(q.media), mediaEl(q.media_extra), mediaEl(q.media_hint), h('p', { class: 'qp-text' }, q.text ?? ''), body,
      hintBar.childNodes.length || bought[q.n] ? hintBar : null);
    if (bought[q.n]) applyHint(q, bought[q.n]);
    tick();
    timer = window.setInterval(tick, 250);
  }

  function reveal(q: Question, r: any, answer: unknown) {
    const verdict = r.timeout ? T.timeout : r.ok ? T.correct : T.wrong;
    const box = h('div', { class: `qp-verdict ${r.ok ? 'ok' : 'bad'}` }, h('b', {}, verdict), r.points ? h('span', {}, fill(T.points, { points: r.points })) : null);
    if (q.type === 'choice') {
      root.querySelectorAll<HTMLElement>('.qp-opt').forEach(b => {
        const k = Number(b.dataset.k);
        if (r.options) { const o = r.options[k - 1]; const s = b.querySelector('span'); const text = typeof o === 'string' ? o : o?.text; if (s && text) s.textContent = text; }
        if (k === r.correct) b.classList.add('ok'); else if (k === answer) b.classList.add('bad');
      });
    }
    const after = h('div', { class: 'qp-after' },
      box,
      q.type === 'open' && !r.ok && r.answer ? h('p', {}, fill(T.rightAnswer, { answer: r.answer })) : null,
      mediaEl(r.answer_media),
      r.explanation ? h('p', { class: 'qp-expl' }, r.explanation) : null,
      h('button', { type: 'button', class: 'btn wide', onclick: () => (r.finished ? opts.onFinish(r) : next(q)) }, r.finished ? T.finish : T.next));
    root.querySelector('.qp-score')!.textContent = fill(T.score, { score });
    const form = root.querySelector('.qp-form'); form?.remove();
    root.querySelector('.qp-timer')?.remove();
    root.querySelector('.qp-hints')?.remove();
    root.append(after);
    after.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function themeOptions(q: Question, options: string[]) {
    render(head(q), h('p', { class: 'qp-text' }, T.themeOptions),
      h('div', { class: 'qp-options' }, ...options.map((o, k) => {
        const b = h('button', { type: 'button', class: 'qp-opt', 'data-k': k + 1 }, h('span', {}, o));
        b.onclick = async () => {
          root.querySelectorAll('button').forEach(x => ((x as HTMLButtonElement).disabled = true));
          b.classList.add('picked');
          try {
            const r = await api.answer(q.n, k + 1, 'theme_options');
            score += r.points ?? 0;
            root.querySelectorAll<HTMLElement>('.qp-opt').forEach(x => {
              const kk = Number(x.dataset.k);
              if (kk === r.correct) x.classList.add('ok'); else if (kk === k + 1) x.classList.add('bad');
            });
            setTimeout(() => opts.onFinish(r), 900);
          } catch (e) { fail(e); }
        };
        return b;
      })));
  }

  const next = (q: Question) => show(qs.findIndex(x => x.n === q.n) + 1);

  if (start.state.theme_pending && start.state.theme_options) themeOptions(qs[qs.length - 1], start.state.theme_options);
  else show(start.state.next_q - 1);

  return () => { alive = false; stop(); root.replaceChildren(); root.classList.remove('qp'); };
}
